// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import type {VoiceEngine} from '@app/features/voice/engine/native_voice_engine/VoiceEngine';
import {ScreenShareSubscriptionManager} from '@app/features/voice/engine/ScreenShareSubscriptionManager';
import {VideoSubscriptionManager} from '@app/features/voice/engine/VideoSubscriptionManager';
import type {
	VideoQualityLevel,
	VoiceRemoteTrackSubscriptionController,
	VoiceSubscriptionContext,
} from '@app/features/voice/engine/VoiceSubscriptionStateMachine';
import type {Room} from 'livekit-client';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('VoiceSubscriptionManager');

export type {VideoQualityLevel};

class VoiceSubscriptionManager {
	private room: Room | null = null;
	private videoManager: VideoSubscriptionManager;
	private screenShareManager: ScreenShareSubscriptionManager;
	private nativeController: VoiceRemoteTrackSubscriptionController | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.videoManager = new VideoSubscriptionManager();
		this.screenShareManager = new ScreenShareSubscriptionManager();
		logger.debug('Initialized');
	}

	setRoom(room: Room | null): void {
		if (this.room === room) return;
		if (this.room) {
			this.cleanup();
		}
		if (room) {
			this.setNativeEngine(null);
		}
		runInAction(() => {
			this.room = room;
		});
		this.videoManager.setRoom(room);
		this.screenShareManager.setRoom(room);
		if (room) {
			logger.info('Room set', {participantCount: room.remoteParticipants.size});
		} else {
			logger.info('Room cleared');
		}
	}

	setNativeEngine(engine: Pick<VoiceEngine, 'setRemoteTrackSubscription'> | null): void {
		this.nativeController = engine
			? {
					setRemoteTrackSubscription: (options) => {
						void engine.setRemoteTrackSubscription(options).catch((error) => {
							logger.warn('Native remote track subscription update failed', {
								participantIdentity: options.participantIdentity,
								source: options.source,
								subscribed: options.subscribed,
								error,
							});
						});
					},
				}
			: null;
		this.videoManager.setNativeController(this.nativeController);
		this.screenShareManager.setNativeController(this.nativeController);
	}

	cleanup(): void {
		logger.debug('Cleaning up all subscriptions');
		this.videoManager.cleanup();
		this.screenShareManager.cleanup();
		this.setNativeEngine(null);
		runInAction(() => {
			this.room = null;
		});
		this.videoManager.setRoom(null);
		this.screenShareManager.setRoom(null);
		logger.info('All subscriptions cleaned up');
	}

	subscribeToVideo(
		participantIdentity: string,
		element: HTMLElement | null,
		initialQuality: VideoQualityLevel = 'low',
	): void {
		this.videoManager.subscribe(participantIdentity, element, initialQuality);
	}

	unsubscribeFromVideo(participantIdentity: string): void {
		this.videoManager.unsubscribe(participantIdentity);
	}

	setVideoEnabled(participantIdentity: string, enabled: boolean): void {
		this.videoManager.setEnabled(participantIdentity, enabled);
	}

	setVideoQuality(participantIdentity: string, quality: VideoQualityLevel): void {
		this.videoManager.setQuality(participantIdentity, quality);
	}

	subscribeToScreenShare(
		participantIdentity: string,
		element: HTMLElement | null,
		context: VoiceSubscriptionContext = 'carousel',
	): void {
		this.screenShareManager.subscribe(participantIdentity, element, context);
	}

	unsubscribeFromScreenShare(participantIdentity: string): void {
		this.screenShareManager.unsubscribe(participantIdentity);
	}

	setScreenShareContext(participantIdentity: string, context: VoiceSubscriptionContext): void {
		this.screenShareManager.setContext(participantIdentity, context);
	}

	isVideoSubscribed(participantIdentity: string): boolean {
		return this.videoManager.isSubscribed(participantIdentity);
	}

	isScreenShareSubscribed(participantIdentity: string): boolean {
		return this.screenShareManager.isSubscribed(participantIdentity);
	}

	reattachScreenShareAfterPublish(participantIdentity: string): void {
		this.screenShareManager.reattachAfterPublish(participantIdentity);
	}

	reattachVideoAfterPublish(participantIdentity: string): void {
		this.videoManager.reattachAfterPublish(participantIdentity);
	}

	getVideoQuality(participantIdentity: string): VideoQualityLevel | null {
		return this.videoManager.getQuality(participantIdentity);
	}

	getScreenShareContext(participantIdentity: string): VoiceSubscriptionContext | null {
		return this.screenShareManager.getContext(participantIdentity);
	}
}

const instance = new VoiceSubscriptionManager();

(
	window as typeof window & {
		_voiceSubscriptionManager?: VoiceSubscriptionManager;
	}
)._voiceSubscriptionManager = instance;

export default instance;
